import type { WatchStopHandle } from "vue"

import { InterfaceSelection } from "~/classes/entity/InterfaceSelection"
import { InterfaceFile } from "~/classes/file/InterfaceFile"
import { sendFilePropertiesRequests } from "~/classes/handlers/file/properties"

export interface ReactiveFileOptions {
  /**
   * If true, the file properties will be requested immediately after the component is mounted
   * @default false
   */
  immediate?: boolean
  /**
   * If true, the file properties will not be requested from the server
   * @default false
   */
  noRequest?: boolean
}

/**
 * Creates a reactive file object that automatically subscribes to file changes and requests
 * the requested files from the server
 *
 * Improvements:
 */
export default function ({ file, selection, options, pathSelection }: {
  file: ComputedRef<InterfaceFile | undefined>
  selection: InterfaceSelection
  pathSelection?: InterfaceSelection
  options: ReactiveFileOptions
}) {
  const reactiveFile: Ref<InterfaceFile | undefined> = ref(file.value)
  let watcher: WatchStopHandle

  const requestSelectedFileProperties = () => {
    const { isOffline } = useCache()

    if (options.noRequest)
      return
    if (!reactiveFile.value)
      throw new Error("Cannot request file properties when file is undefined")

    sendFilePropertiesRequests([{
      requestId: generateWorkerRequestId(),
      fileId: reactiveFile.value.fileId,
      offline: isOffline(),
      selection: selection.inner(),
      pathSelection: pathSelection?.inner() ?? InterfaceSelection.minimumPathSelection().inner(),
    }])
  }

  const onFilePropertiesChanged = (file: FileEvent) => {
    if (file.fileId !== reactiveFile.value?.fileId)
      return console.warn("Reactive file received properties for a different file", file, reactiveFile.value)
    if (!reactiveFile.value)
      reactiveFile.value = new InterfaceFile(file)
    reactiveFile.value.mergeWithNewer(new InterfaceFile(file))
  }

  const onFileChanged = (newFile?: InterfaceFile, oldFile?: InterfaceFile) => {
    const { addEntityEventListener, removeEntityEventListener } = useEvents()

    // console.trace("Reactive file changed", newFile, oldFile)

    if (!newFile && !oldFile)
      return

    if (newFile?.fileId === reactiveFile.value?.fileId)
      reactiveFile.value?.mergeWithNewer(newFile)

    if (newFile?.fileId === oldFile?.fileId)
      return

    if (oldFile)
      removeEntityEventListener("fileProperties", oldFile.fileId, onFilePropertiesChanged)

    if (newFile) {
      reactiveFile.value = newFile
      addEntityEventListener("fileProperties", newFile.fileId, onFilePropertiesChanged)
      requestSelectedFileProperties()
    }
    else {
      reactiveFile.value = undefined
    }
  }

  onMounted(() => {
    const { addEntityEventListener } = useEvents()

    if (reactiveFile.value) {
      const fileId = reactiveFile.value.fileId
      addEntityEventListener("fileProperties", fileId, onFilePropertiesChanged)
    }
    watcher = watch(file, onFileChanged)

    if (options.immediate)
      requestSelectedFileProperties()
  })

  onUnmounted(() => {
    const { removeEntityEventListener } = useEvents()

    if (reactiveFile.value) {
      const fileId = reactiveFile.value.fileId
      removeEntityEventListener("fileProperties", fileId, onFilePropertiesChanged)
    }
    if (isDefined(watcher))
      watcher()
  })

  return reactiveFile
}
